import React, { useEffect, useState } from "react";
import {
  ArrowUpRight,
  ScanLine,
  ChefHat,
  ShoppingCart,
  CalendarDays,
  Sparkles,
  HeartPulse,
  Trophy,
  Share2,
  Info,
  Snowflake,
  Search,
  ClipboardList,
  CheckCircle2,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import PhoneFrame, { APP_SCREENSHOTS } from "../components/site/PhoneFrame";
import { joinWaitlist } from "../lib/api";
import { trackFormAction } from "../lib/tracker";

const PLAY_URL = "https://play.google.com/store/apps/details?id=mise.at.home";
const SITE_URL = "https://www.misehome.ca";
const LOGO = "/images/logos/at-home.png";

// One icon per entry in t.atHome.features, same order.
const featureIcons = [ScanLine, ChefHat, ShoppingCart, CalendarDays, Sparkles, HeartPulse, Trophy, Share2];
const showcaseIcons = [Snowflake, Search, ClipboardList];
const GALLERY = [
  APP_SCREENSHOTS.dashboard,
  APP_SCREENSHOTS.onboarding,
  APP_SCREENSHOTS.recipeDetail,
  APP_SCREENSHOTS.recipeSteps,
  APP_SCREENSHOTS.groceryList,
];

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#E8490F]">{children}</p>
);

// A store-style button for the Google Play listing.
const PlayButton = ({ p, testId }) => (
  <a
    href={PLAY_URL}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={p.playCta}
    className="inline-flex items-center gap-3 rounded-xl border border-white/25 bg-black px-4 py-2 text-left text-white transition-colors hover:border-[#E8490F]"
    data-testid={testId}
    data-track={testId}
  >
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path d="M4 2.8v18.4c0 .6.6 1 1.2.7l15.9-9.2c.5-.3.5-1.1 0-1.4L5.2 2.1C4.6 1.8 4 2.2 4 2.8z" fill="#E8490F" />
    </svg>
    <span className="leading-none">
      <span className="block text-[10px] uppercase tracking-[0.08em] text-white/75">{p.playSmall}</span>
      <span className="mt-0.5 block text-[17px] font-semibold tracking-tight">{p.playBig}</span>
    </span>
  </a>
);

const MiseAtHome = () => {
  const { t } = useLanguage();
  const p = t.atHome;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [p.metaTitle]);

  return (
    <div data-testid="page-at-home">
      {/* HERO: logo, headline, Google Play, and a fan of three phones */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute left-1/2 -top-52 h-[420px] w-[700px] -translate-x-1/2 rounded-full bg-[#E8490F]/15 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative pt-10 md:pt-12 text-center">
          <img src={LOGO} alt={p.heading} className="mx-auto h-16 md:h-[4.5rem] w-auto" />
          <h1 className="mt-5 mx-auto max-w-3xl text-4xl sm:text-5xl lg:text-[3.1rem] font-semibold tracking-tight leading-[1.04] [text-wrap:balance]">
            {p.subheading}
            <span className="block mt-1 mise-serif mise-accent text-[0.8em]">{p.subheadingItalic}</span>
          </h1>
          <p className="mt-4 mx-auto max-w-2xl text-base md:text-[17px] text-white/70 leading-relaxed">{p.intro}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <PlayButton p={p} testId="at-home-play-hero" />
            <a
              href={SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mise-pill-secondary"
              data-testid="at-home-learn-more"
              data-track="at-home-site"
            >
              {p.siteCta}
              <ArrowUpRight size={16} />
            </a>
          </div>
          {/* PhoneFrame is position:relative, so each phone sits in its own positioned holder */}
          <div className="group relative mx-auto mt-10 h-[300px] md:h-[380px] max-w-3xl overflow-hidden" aria-hidden>
            <div className="absolute left-1/2 top-10 w-[140px] md:w-[190px] -translate-x-[120%] -rotate-[10deg] transition-transform duration-500 group-hover:-translate-x-[135%] group-hover:-rotate-[13deg]">
              <PhoneFrame src={APP_SCREENSHOTS.dashboard} />
            </div>
            <div className="absolute left-1/2 top-10 w-[140px] md:w-[190px] translate-x-[20%] rotate-[10deg] transition-transform duration-500 group-hover:translate-x-[35%] group-hover:rotate-[13deg]">
              <PhoneFrame src={APP_SCREENSHOTS.groceryList} />
            </div>
            <div className="absolute left-1/2 top-0 z-10 w-[160px] md:w-[210px] -translate-x-1/2">
              <PhoneFrame src={APP_SCREENSHOTS.onboarding} />
            </div>
            <div className="absolute inset-x-0 bottom-0 z-20 h-32 bg-gradient-to-t from-[#111111] to-transparent" />
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="pb-12 md:pb-16">
        <div className="mise-container">
          <div
            className="mx-auto max-w-5xl grid gap-4 md:grid-cols-[auto_1fr_auto] md:items-center rounded-2xl border border-[#E8490F]/40 bg-gradient-to-r from-[#2b1810] to-[#181818] px-6 py-5 md:px-7"
            data-testid="at-home-stat"
          >
            <p className="text-5xl font-semibold tracking-tight text-[#E8490F] leading-none">{p.stat.value}</p>
            <div>
              <p className="font-semibold text-white">{p.stat.label}</p>
              <p className="mt-1 text-[11px] text-white/40">{p.stat.source}</p>
            </div>
            <p className="max-w-sm text-sm text-white/65 leading-relaxed">{p.why}</p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="at-home-features">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl">
            <Eyebrow>{p.featuresEyebrow}</Eyebrow>
            <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white [text-wrap:balance]">
              {p.featuresHeading}
            </h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {p.features.map((f, i) => {
                const Icon = featureIcons[i];
                return (
                  <div key={f.title} className="rounded-2xl border border-[#333333] bg-[#181818] p-4" data-testid={`at-home-feature-${i}`}>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#E8490F]/15 text-[#E8490F]">
                      <Icon size={17} />
                    </span>
                    <h3 className="mt-3 text-[15px] font-semibold text-white">{f.title}</h3>
                    <p className="mt-1 text-[13px] text-white/65 leading-relaxed">{f.text}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 flex items-start gap-2 text-xs text-white/45 leading-relaxed" data-testid="at-home-nutrition-note">
              <Info size={14} className="mt-0.5 flex-shrink-0 text-[#E8490F]" />
              {p.nutritionNote}
            </p>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="at-home-gallery">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl">
            <Eyebrow>{p.galleryEyebrow}</Eyebrow>
            <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white">{p.galleryHeading}</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-5">
              {GALLERY.map((src, i) => (
                <figure key={src} className="m-0">
                  <PhoneFrame src={src} alt={p.gallery[i]} />
                  <figcaption className="mt-2.5 text-center text-xs text-white/50">{p.gallery[i]}</figcaption>
                </figure>
              ))}
            </div>
            <p className="mt-4 text-xs text-white/45">{p.galleryNote}</p>
          </div>
        </div>
      </section>

      {/* SMART GROCERY */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="at-home-grocery">
        <div className="mise-container">
          <div className="mx-auto max-w-5xl grid gap-10 lg:grid-cols-[1.3fr_1fr] items-center">
            <div className="min-w-0">
              <Eyebrow>{p.showcaseEyebrow}</Eyebrow>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white">{p.showcaseHeading}</h2>
              <p className="mt-3 text-white/65 leading-relaxed">{p.showcaseBody}</p>
              <ul className="mt-6 space-y-4">
                {p.showcasePoints.map((pt, i) => {
                  const Icon = showcaseIcons[i];
                  return (
                    <li key={pt.title} className="flex gap-3">
                      <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#E8490F]/15 text-[#E8490F]">
                        <Icon size={17} />
                      </span>
                      <div>
                        <p className="font-semibold text-white">{pt.title}</p>
                        <p className="mt-0.5 text-sm text-white/60 leading-relaxed">{pt.text}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="relative flex justify-center">
              <div className="pointer-events-none absolute h-72 w-72 rounded-full bg-[#E8490F]/15 blur-3xl" aria-hidden />
              <PhoneFrame src={APP_SCREENSHOTS.groceryList} alt={p.gallery[4]} className="relative w-full max-w-[230px]" />
            </div>
          </div>
        </div>
      </section>

      {/* GET THE APP + WAITLIST FOR OTHER COUNTRIES */}
      <section
        id="mise-at-home-waitlist"
        className="pb-12 md:pb-16 scroll-mt-24"
        data-testid="at-home-get"
      >
        <div className="mise-container">
          <div className="mx-auto max-w-5xl grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-[#E8490F]/40 bg-gradient-to-br from-[#2b1810] to-[#181818] p-6 md:p-7">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">{p.readyHeading}</h2>
              <p className="mt-2 text-white/65 leading-relaxed">{p.readySub}</p>
              <div className="mt-5">
                <PlayButton p={p} testId="at-home-play-bottom" />
              </div>
            </div>
            <div className="rounded-2xl border border-[#333333] bg-[#181818] p-6 md:p-7">
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-white">{p.waitlistHeading}</h2>
              <p className="mt-2 text-sm text-white/65 leading-relaxed">{p.waitlistSub}</p>
              <WaitlistForm copy={p.waitlist} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const WaitlistForm = ({ copy }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | success
  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    const value = email.trim();
    if (!value) return;
    setState("sending");
    try {
      await joinWaitlist({ email: value, source: "mise-at-home-other-countries" });
      trackFormAction("waitlist:submit");
      setState("success");
      setEmail("");
    } catch (err) {
      console.error(err);
      toast.error(copy.error);
      setState("idle");
    }
  };

  if (state === "success") {
    return (
      <div
        className="mt-5 flex items-center gap-3 rounded-full border border-[#E8490F]/50 bg-[#E8490F]/10 px-5 py-3"
        data-testid="waitlist-success"
        role="status"
        aria-live="polite"
      >
        <CheckCircle2 className="text-[#E8490F]" size={18} />
        <span className="text-white/90 text-sm">{copy.success}</span>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-5 flex flex-col sm:flex-row gap-2.5" data-testid="waitlist-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={copy.placeholder}
        aria-label={copy.placeholder}
        className="min-w-0 flex-1 bg-[#111111] border border-[#444444] focus:border-[#E8490F] outline-none rounded-full px-5 py-2.5 text-white placeholder:text-white/30"
        data-testid="waitlist-email"
      />
      <button
        type="submit"
        disabled={state === "sending"}
        className="mise-pill-primary !py-2.5 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed justify-center"
        data-testid="waitlist-submit"
      >
        {state === "sending" ? copy.sending : copy.submit}
        {state !== "sending" && <Send size={15} />}
      </button>
    </form>
  );
};

export default MiseAtHome;
